import {
  useZAFClient,
  zendeskApi,
  useCreateTrigger,
  zendeskQueries,
  createTriggerCategory,
  getTriggerCategories,
} from '@/features/zendesk-app'
import { useWebhook } from '../..//hooks/useWebhook'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useGetRoute } from '@/lib/react-query'

export const TriggerCategoryName = 'Knots Studio'

export const useTriggersCategory = () => {
  const createStudioCategory = async () => {
    const { trigger_category } = await createTriggerCategory({
      name: TriggerCategoryName,
      position: 0,
    })

    return trigger_category
  }

  const { data: category } = useQuery({
    queryKey: ['app', 'zendesk', 'trigger_category'],
    queryFn: async () => {
      const { trigger_categories } = await getTriggerCategories()
      const category = trigger_categories.find((category) => category.name === TriggerCategoryName)
      return category ?? (await createStudioCategory())
    },
  })

  return category
}

export const useTriggers = (projectId: number, routeId: number): zendeskApi.Trigger[] => {
  const { webhook } = useWebhook(projectId, routeId)
  const category = useTriggersCategory()
  const query = useQuery(zendeskQueries.triggers.list(webhook && category?.id))

  return useMemo(
    () =>
      query.data?.triggers.filter((trigger) =>
        trigger.actions.some((action) => {
          return action.value?.[0] === webhook?.id
        })
      ) ?? [],
    [webhook, query.data]
  )
}

export const useOpenTrigger = () => {
  const zafClient = useZAFClient()!
  return async (id: number) => {
    const context = await zafClient.context()
    const subdomain = context.account.subdomain
    window.open(`https://${subdomain}.zendesk.com/admin/objects-rules/rules/triggers/${id}`)
  }
}

const useGetTriggerPayload = () => {
  const zafClient = useZAFClient()

  return async () => {
    if (!zafClient) throw 'No ZAFClient'
    const { settings } = await zafClient.metadata()

    try {
      const value = settings.triggerPayload
      return JSON.parse(value)
    } catch {
      return {}
    }
  }
}

const useGetTriggerBody = (projectId: number, routeId: number) => {
  const zafClient = useZAFClient()
  const replacePlaceholders = useReplacePlaceholders(projectId, routeId)

  return async () => {
    if (!zafClient) throw 'No ZAFClient'
    const { settings } = await zafClient.metadata()

    try {
      const value = settings.triggerBody
      return JSON.parse(replacePlaceholders(value))
    } catch {
      return undefined
    }
  }
}

const useReplacePlaceholders = (projectId: number, routeId: number) => {
  const { webhook } = useWebhook(projectId, routeId)
  const {
    data: { route },
  } = useGetRoute(projectId, routeId)
  const category = useTriggersCategory()

  return (value: string) => {
    return value
      .replaceAll('{{webhookId}}', webhook!.id)
      .replaceAll('{{categoryId}}', category!.id)
      .replaceAll('{{routeName}}', route.name)
  }
}

export const useCreateNewTrigger = (projectId: number, routeId: number) => {
  const { webhook } = useWebhook(projectId, routeId)
  const {
    data: { route },
  } = useGetRoute(projectId, routeId)
  const category = useTriggersCategory()
  const mutation = useCreateTrigger()
  const getTriggerPayload = useGetTriggerPayload()
  const getTriggerBody = useGetTriggerBody(projectId, routeId)

  const createNewTrigger = async () => {
    const payload = await getTriggerPayload()
    const body = await getTriggerBody()

    return mutation.mutate(
      body ?? {
        category_id: category?.id,
        active: true,
        title: `${route.name} Trigger`,
        conditions: {
          all: [
            {
              field: 'current_tags',
              operator: 'includes',
              value: 'knots_studio',
            },
            {
              field: 'current_tags',
              operator: 'not_includes',
              value: 'knots_studio_done',
            },
            {
              field: 'status',
              operator: 'value',
              value: 'solved',
            },
          ],
          any: [],
        },
        actions: [
          {
            field: 'notification_webhook',
            value: [webhook!.id, JSON.stringify(payload)],
          },
          {
            field: 'current_tags',
            value: 'knots_studio_done',
          },
        ],
      }
    )
  }

  return [createNewTrigger, mutation.isPending] as const
}
